/* INDICADORES
 * Quatro campos, quatro significados: mint informa, telha perde,
 * areia constata, mel custa. */

import { Hexinho } from '@/componentes/hexagono';
import { Indicador, type TomRegua } from '@/componentes/indicador';
import type { CampoDeCor } from '@/componentes/celula';
import { Valor } from '@/componentes/valor';
import type { Cenario, Parametros, Recomendacao } from '@/dados';
import { formatarMoeda, formatarPercentual, formatarPercentualPontos } from '@/design/formato';

import { escala, quantia } from './grandezas';

/** Campo de cor de um indicador conforme o sinal da grandeza. */
function campoDoSinal(valor: number | null, indefinido: boolean): CampoDeCor {
  if (indefinido || valor === null) return 'nata';
  return valor < 0 ? 'telha' : 'mint';
}

function tomDoSinal(valor: number | null): TomRegua {
  return valor !== null && valor < 0 ? 'negativo' : 'acao';
}

function corDoSinal(valor: number | null): string {
  return valor !== null && valor < 0 ? 'var(--negativo)' : 'var(--acao)';
}

export function Indicadores({
  rec,
  p,
  atual,
  semPreco,
  semVolume,
}: {
  rec: Recomendacao;
  p: Parametros;
  /** `null` quando o motor recusa opinar: não há preço deste projeto a decompor. */
  atual: Cenario | null;
  semPreco: boolean;
  semVolume: boolean;
}) {
  // Uma leitura só do cenário, no topo. Sem cenário toda grandeza derivada
  // dele é AUSÊNCIA, não zero: o motor recusou opinar e a régua não tem onde
  // se apoiar. `?? null` e não `?? 0` porque zero aqui seria uma afirmação.
  const mcUnit = atual?.mcUnit ?? null;
  const mcPct = atual?.mcPct ?? null;
  const lucroUnit = atual?.lucroUnit ?? null;
  const lucroPct = atual?.lucroPct ?? null;
  const peFat = atual?.peFat ?? null;
  const peQtd = atual?.peQtd ?? null;
  const ticketDoCenario = atual?.ticket ?? null;
  const fatMes = atual?.mensal.faturamento ?? null;
  const lucroMes = atual?.mensal.lucro ?? null;
  // Sai de `p`, não do cenário: volume existe com ou sem preço.
  const qtdMes = p.V12 / 12;

  return (
    <div className="favo @coluna-larga:grid-cols-2 @painel-largo:grid-cols-4">
      <Indicador
        rotulo="Margem de contribuição"
        campo={campoDoSinal(mcUnit, semPreco)}
        apoio={
          semPreco
            ? 'Informe o valor de venda em Dados do projeto.'
            : `${formatarMoeda(mcUnit)} por projeto, antes da estrutura.`
        }
        referencia={
          'sugerido' in rec ? `mínimo ${formatarPercentual(rec.sugerido.mcPct)}` : 'escala 0 a 50%'
        }
        regua={{
          fracao: escala(semPreco ? null : mcPct, 0, 0.5),
          meta: 'sugerido' in rec ? escala(rec.sugerido.mcPct, 0, 0.5) : undefined,
          tom: tomDoSinal(mcUnit),
        }}
        marcador={<Hexinho cor={corDoSinal(mcUnit)} />}
        assenta
        atraso={120}
      >
        <Valor
          valor={semPreco ? null : mcPct}
          formato="percentual"
          tamanho="titulo"
          tom={mcUnit !== null && mcUnit < 0 ? 'negativo' : 'neutro'}
        />
      </Indicador>

      <Indicador
        rotulo="Lucro líquido"
        campo={campoDoSinal(lucroUnit, lucroUnit === null)}
        apoio={
          lucroUnit === null
            ? semPreco
              ? 'Informe o valor de venda em Dados do projeto.'
              : 'Informe o volume em Indicadores ou preencha a Sazonalidade.'
            : `${formatarMoeda(lucroUnit)} por projeto, ${formatarMoeda(lucroMes)} no mês.`
        }
        referencia={`meta ${formatarPercentualPontos(p.lucroAlvo)}`}
        regua={{
          fracao: escala(lucroPct, -0.2, 0.4),
          meta: escala(p.lucroAlvo / 100, -0.2, 0.4),
          tom: tomDoSinal(lucroUnit),
        }}
        marcador={<Hexinho cor={corDoSinal(lucroUnit)} />}
        assenta
        atraso={160}
      >
        <Valor
          valor={lucroPct}
          formato="percentual"
          tamanho="titulo"
          tom={lucroUnit !== null && lucroUnit < 0 ? 'negativo' : 'neutro'}
        />
      </Indicador>

      <Indicador
        rotulo="Faturamento no mês"
        campo="areia"
        apoio={
          semPreco
            ? 'Informe o valor de venda em Dados do projeto.'
            : semVolume
              ? 'Informe o volume em Indicadores ou preencha a Sazonalidade.'
              : `${formatarMoeda(ticketDoCenario)} por projeto, ${quantia(qtdMes)} projetos no mês.`
        }
        referencia={
          peFat === null ? 'sem equilíbrio a alcançar' : `equilíbrio ${formatarMoeda(peFat)}`
        }
        regua={{
          fracao: escala(
            semPreco || semVolume ? null : fatMes,
            0,
            Math.max(fatMes ?? 0, peFat ?? 0) * 1.25 || 1,
          ),
          meta:
            peFat === null ? undefined : escala(peFat, 0, Math.max(fatMes ?? 0, peFat) * 1.25 || 1),
          tom: 'neutro',
        }}
        marcador={<Hexinho cor="var(--areia-forte)" />}
        assenta
        atraso={200}
      >
        <Valor valor={semPreco || semVolume ? null : fatMes} formato="moeda" tamanho="titulo" />
      </Indicador>

      <Indicador
        rotulo="Ponto de equilíbrio"
        campo="mel"
        apoio={
          peQtd === null
            ? 'Depende de margem de contribuição positiva.'
            : `projetos por mês só para a estrutura se pagar. Hoje entram ${quantia(qtdMes)}.`
        }
        referencia={semVolume ? 'volume não informado' : `hoje ${quantia(qtdMes)} por mês`}
        regua={{
          fracao: escala(semVolume ? null : qtdMes, 0, Math.max(qtdMes, peQtd ?? 0) * 1.25 || 1),
          meta: peQtd === null ? undefined : escala(peQtd, 0, Math.max(qtdMes, peQtd) * 1.25 || 1),
          tom: 'atencao',
        }}
        marcador={<Hexinho cor="var(--atencao)" />}
        assenta
        atraso={240}
      >
        <Valor valor={peQtd} formato="numero" decimais={1} tamanho="titulo" />
      </Indicador>
    </div>
  );
}
